import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  type CollectionReference,
} from 'firebase/firestore';

import { db } from '@/firebase-config';
import type { RehearsalScene, ScheduleEvent } from '@/features/scheduler/types';
import type { ProjectScope } from '@/features/sync/project-scope';
import {
  projectScenesCollectionPath,
  projectScheduleCollectionPath,
} from '@/features/sync/schedule-paths';
import { logger } from '@/utils/logger';

import type {
  ProjectScheduleClient,
  RehearsalScenesListener,
  ScheduleEventsListener,
} from '../contracts/project-schedule-client';

type ScheduleSnapshot = {
  docs: Array<{ id: string; data: () => ScheduleEvent }>;
};

type RehearsalScenesSnapshot = {
  docs: Array<{ id: string; data: () => RehearsalScene }>;
};

function scheduleCollection(scope: ProjectScope): CollectionReference<ScheduleEvent> {
  return collection(db, projectScheduleCollectionPath(scope)) as CollectionReference<ScheduleEvent>;
}

function scenesCollection(scope: ProjectScope): CollectionReference<RehearsalScene> {
  return collection(db, projectScenesCollectionPath(scope)) as CollectionReference<RehearsalScene>;
}

function scheduleEventsFromSnapshot(snapshot: ScheduleSnapshot): ScheduleEvent[] {
  return snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }));
}

function rehearsalScenesFromSnapshot(snapshot: RehearsalScenesSnapshot): RehearsalScene[] {
  return snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }));
}

export class FirebaseProjectScheduleClient implements ProjectScheduleClient {
  async listScheduleEvents(scope: ProjectScope): Promise<ScheduleEvent[]> {
    const eventsQuery = query(scheduleCollection(scope), orderBy('date', 'asc'));
    const snapshot = await getDocs(eventsQuery);
    return scheduleEventsFromSnapshot(snapshot as ScheduleSnapshot);
  }

  async listRehearsalScenes(scope: ProjectScope): Promise<RehearsalScene[]> {
    const snapshot = await getDocs(scenesCollection(scope));
    return rehearsalScenesFromSnapshot(snapshot as RehearsalScenesSnapshot);
  }

  subscribeScheduleEvents(scope: ProjectScope, listener: ScheduleEventsListener) {
    const eventsQuery = query(scheduleCollection(scope), orderBy('date', 'asc'));
    return onSnapshot(
      eventsQuery,
      (snapshot) => {
        listener(scheduleEventsFromSnapshot(snapshot as ScheduleSnapshot));
      },
      (error) => {
        logger.error('[FirebaseProjectScheduleClient] Events subscription error:', error);
      },
    );
  }

  subscribeRehearsalScenes(scope: ProjectScope, listener: RehearsalScenesListener) {
    return onSnapshot(
      scenesCollection(scope),
      (snapshot) => {
        listener(rehearsalScenesFromSnapshot(snapshot as RehearsalScenesSnapshot));
      },
      (error) => {
        logger.error('[FirebaseProjectScheduleClient] Scenes subscription error:', error);
      },
    );
  }
}
